import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Auth Pages
import Login from './pages/auth/Login';
import VerifyEmail from './pages/auth/VerifyEmail';
import SetPassword from './pages/auth/SetPassword';
import ForgotPassword from './pages/auth/ForgotPassword';

// Dashboard Pages
import DashboardList from './pages/dashboard/DashboardList';
import DashboardBuilder from './pages/dashboard/DashboardBuilder';

// Data Pages
import DataSourceList from './pages/data/DataSourceList';
import DataSourceDetail from './pages/data/DataSourceDetail';
import DataUpload from './pages/data/DataUpload';
import DataSourceCreate from './pages/data/DataSourceCreate';
import RelationshipEditor from './pages/data/RelationshipEditor';
import MetricBuilder from './pages/data/MetricBuilder';
import MartBuilder from './pages/data/MartBuilder';
import PipelineStatus from './pages/data/PipelineStatus';

// AI Pages
import AIChat from './pages/ai/AIChat';

// Admin Pages
import UserManagement from './pages/admin/UserManagement';
import CompanySettings from './pages/admin/CompanySettings';
import BillingUsage from './pages/admin/BillingUsage';
import Companies from './pages/admin/Companies';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, _hasHydrated } = useAuthStore();
  
  // Wait for hydration and loading to complete before making auth decisions
  if (!_hasHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function App() {
  return (
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/verify" element={<VerifyEmail />} />
        <Route path="/auth/set-password" element={<SetPassword />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<SetPassword isReset />} />
      </Route>
      
      {/* Protected Routes */}
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        {/* Dashboards */}
        <Route path="/" element={<Navigate to="/dashboards" replace />} />
        <Route path="/dashboards" element={<DashboardList />} />
        <Route path="/dashboards/:id" element={<DashboardBuilder />} />
        <Route path="/dashboards/new" element={<DashboardBuilder />} />
        
        {/* Data Sources */}
        <Route path="/data" element={<DataSourceList />} />
        <Route path="/data/new" element={<DataSourceCreate />} />
        <Route path="/data/upload" element={<DataUpload />} />
        <Route path="/data/:id" element={<DataSourceDetail />} />
        
        {/* Data Pipeline & Semantic Layer */}
        <Route path="/data/relationships" element={<RelationshipEditor />} />
        <Route path="/data/metrics" element={<MetricBuilder />} />
        <Route path="/data/marts" element={<MartBuilder />} />
        <Route path="/data/pipeline" element={<PipelineStatus />} />
        
        {/* AI Chat */}
        <Route path="/ai" element={<AIChat />} />
        <Route path="/ai/:conversationId" element={<AIChat />} />
        
        {/* Admin */}
        <Route path="/admin/companies" element={<Companies />} />
        <Route path="/admin/users" element={<UserManagement />} />
        <Route path="/admin/settings" element={<CompanySettings />} />
        <Route path="/admin/billing" element={<BillingUsage />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboards" replace />} />
    </Routes>
  );
}

export default App;
